<template>
  <div class="spare-part-detail-page">
    <!-- Loading State -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-3"></div>
        <p class="text-gray-600">{{ t('sparePartDetails.loading', 'Loading...') }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="!part" class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center max-w-md mx-auto px-4">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 mb-2">{{ t('sparePartDetails.notFound', 'Part Not Found') }}</h2>
        <p class="text-gray-600 mb-4">{{ t('sparePartDetails.notFoundMessage', 'The spare part you are looking for does not exist.') }}</p>
        <NuxtLink to="/spare-parts" class="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700">
          ← {{ t('sparePartDetails.browseAll', 'Back to Spare Parts') }}
        </NuxtLink>
      </div>
    </div>

    <!-- Spare Part Detail Content -->
    <div v-else>
      <!-- Compact Header Bar -->
      <div class="bg-gray-800 text-white print:hidden">
        <div class="container mx-auto px-4 py-4">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <!-- Breadcrumb -->
            <nav class="flex items-center text-sm">
              <NuxtLink to="/" class="text-gray-400 hover:text-white">{{ t('sparePartDetails.breadcrumb.home', 'Home') }}</NuxtLink>
              <span class="mx-2 text-gray-500">/</span>
              <NuxtLink to="/spare-parts" class="text-gray-400 hover:text-white">{{ t('sparePartDetails.breadcrumb.spareParts', 'Spare Parts') }}</NuxtLink>
              <span class="mx-2 text-gray-500">/</span>
              <span class="text-white truncate max-w-[200px]">{{ part.part_number || part.name }}</span>
            </nav>
            <!-- Quick Actions -->
            <div class="flex items-center gap-2">
              <button @click="printPage" class="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                </svg>
                {{ t('sparePartDetails.print', 'Print') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="bg-gradient-to-b from-slate-50 to-slate-100">
        <div class="container mx-auto px-4 py-8">
          <!-- Title Row -->
          <div class="bg-white rounded-xl shadow-sm p-6 mb-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <span v-if="part.part_number" class="text-sm font-semibold text-blue-600">Part #{{ part.part_number }}</span>
                <span v-if="part.brand" class="text-sm text-gray-500">{{ part.brand.name }}</span>
              </div>
              <h1 class="text-2xl lg:text-3xl font-bold text-gray-900">{{ part.name }}</h1>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <span :class="getStockStatusClass(part.stock_quantity)" class="px-3 py-1.5 text-sm font-bold rounded">
                {{ getStockStatusLabel(part.stock_quantity) }}
              </span>
              <span v-if="part.condition" class="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded capitalize">
                {{ part.condition }}
              </span>
            </div>
          </div>

          <!-- Two Column Layout: Gallery + Info -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Left: Image Gallery -->
            <div class="bg-white rounded-xl shadow-sm p-4">
              <!-- Main Image -->
              <div class="relative aspect-square bg-gray-50 rounded-lg overflow-hidden mb-3 border border-gray-200">
                <img
                  v-if="selectedImage"
                  :src="selectedImage"
                  :alt="part.name"
                  class="w-full h-full object-cover cursor-pointer"
                  @click="openLightbox(selectedImageIndex)"
                  @error="handleImageError"
                />
                <div v-else class="w-full h-full flex items-center justify-center">
                  <svg class="w-20 h-20 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                </div>

                <!-- Image Counter -->
                <div v-if="partImages.length > 1" class="absolute bottom-3 right-3 bg-black/60 text-white px-3 py-1 rounded text-sm">
                  {{ selectedImageIndex + 1 }} / {{ partImages.length }}
                </div>

                <!-- Navigation -->
                <button v-if="partImages.length > 1" @click.stop="prevImage" class="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                </button>
                <button v-if="partImages.length > 1" @click.stop="nextImage" class="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </button>
              </div>

              <!-- Thumbnails -->
              <div v-if="partImages.length > 1" class="flex gap-2 overflow-x-auto pb-2">
                <button
                  v-for="(image, index) in partImages"
                  :key="index"
                  @click="selectedImageIndex = index"
                  class="w-16 h-16 flex-shrink-0 rounded overflow-hidden border-2"
                  :class="selectedImageIndex === index ? 'border-blue-600' : 'border-gray-200 hover:border-gray-400'"
                >
                  <img :src="getImageUrl(image)" :alt="`${part.name} - ${index + 1}`" class="w-full h-full object-cover" @error="handleImageError" />
                </button>
              </div>

              <!-- Share & Social Links -->
              <div class="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 print:hidden">
                <p class="text-sm font-medium text-blue-800 mb-3">{{ t('sparePartDetails.hero.share', 'Share this part') }}</p>
                <div class="flex flex-wrap gap-2">
                  <!-- Facebook -->
                  <a :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`" target="_blank"
                     class="w-9 h-9 bg-[#1877F2] hover:bg-[#166FE5] rounded flex items-center justify-center text-white" title="Share on Facebook">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <!-- WhatsApp -->
                  <a :href="`https://wa.me/?text=${encodeURIComponent(part.name + ' - ' + currentUrl)}`" target="_blank"
                     class="w-9 h-9 bg-[#25D366] hover:bg-[#20BD5A] rounded flex items-center justify-center text-white" title="Share on WhatsApp">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                  <!-- Instagram (Company page) -->
                  <a href="https://www.instagram.com/technomacsnc" target="_blank"
                     class="w-9 h-9 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 rounded flex items-center justify-center text-white" title="Follow on Instagram">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <!-- X (Twitter) -->
                  <a :href="`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(part.name)}`" target="_blank"
                     class="w-9 h-9 bg-black hover:bg-gray-800 rounded flex items-center justify-center text-white" title="Share on X">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                </div>
              </div>
            </div>

            <!-- Right: Key Info + CTA -->
            <div class="space-y-4">
              <!-- Price Display -->
              <div v-if="part.price" class="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-5 shadow-lg">
                <p class="text-sm text-green-100 font-medium mb-1">{{ t('sparePartDetails.price', 'Price') }}</p>
                <p class="text-4xl font-bold text-white">{{ formatPrice(part.price) }}</p>
              </div>
              <div v-else class="bg-gradient-to-r from-slate-600 to-slate-700 rounded-xl p-5 shadow-lg">
                <p class="text-sm text-slate-300 font-medium mb-1">{{ t('sparePartDetails.price', 'Price') }}</p>
                <p class="text-xl font-bold text-white">{{ t('sparePartDetails.contactForPricing', 'Contact for Pricing') }}</p>
              </div>

              <!-- Quick Specs -->
              <div class="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h2 class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  {{ t('sparePartDetails.quickInfo.title', 'Part Details') }}
                </h2>
                <div class="grid grid-cols-2 gap-3">
                  <div v-if="part.part_number" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ t('sparePartDetails.info.partNumber', 'Part Number') }}</p>
                    <p class="font-bold text-slate-800">{{ part.part_number }}</p>
                  </div>
                  <div v-if="part.brand" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ t('sparePartDetails.info.brand', 'Brand') }}</p>
                    <p class="font-bold text-slate-800">{{ part.brand.name }}</p>
                  </div>
                  <div v-if="part.category" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ t('sparePartDetails.info.category', 'Category') }}</p>
                    <p class="font-bold text-slate-800">{{ part.category.name }}</p>
                  </div>
                  <div v-if="part.stock_quantity !== undefined" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ t('sparePartDetails.info.stock', 'In Stock') }}</p>
                    <p class="font-bold text-slate-800">{{ part.stock_quantity }} {{ t('sparePartDetails.units', 'units') }}</p>
                  </div>
                  <div v-if="part.condition" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ t('sparePartDetails.info.condition', 'Condition') }}</p>
                    <p class="font-bold text-slate-800 capitalize">{{ part.condition }}</p>
                  </div>
                  <div v-if="part.location" class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200">
                    <p class="text-xs text-slate-500 uppercase font-medium">{{ t('sparePartDetails.info.location', 'Location') }}</p>
                    <p class="font-bold text-slate-800">{{ part.location }}</p>
                  </div>
                </div>
              </div>

              <!-- CTA Buttons -->
              <div class="bg-white rounded-xl shadow-sm p-5 border border-gray-100 print:hidden">
                <!-- Buttons side by side -->
                <div class="flex gap-3">
                  <button @click="showInquiryModal = true" class="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    {{ t('sparePartDetails.cta.requestQuote', 'REQUEST QUOTE') }}
                  </button>
                  <a href="https://wa.me/393289722811" target="_blank" class="flex-1 py-3 px-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#20BD5A] hover:to-[#0d7d6e] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-500/25">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {{ t('sparePartDetails.cta.contactWhatsApp', 'WhatsApp') }}
                  </a>
                </div>
              </div>

              <!-- Company Contact Box -->
              <div class="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm">
                <h3 class="font-semibold text-blue-900 mb-2">{{ t('sparePartDetails.contact.title', 'Need more information?') }}</h3>
                <p class="text-sm text-blue-800 mb-3">{{ t('sparePartDetails.contact.desc', 'Our team is ready to assist you with compatibility, pricing, and shipping.') }}</p>
                <div class="flex items-center gap-4 text-sm">
                  <a href="mailto:info@technomac-snc.com" class="text-blue-700 hover:underline flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    Email
                  </a>
                  <a href="tel:+393331234567" class="text-blue-700 hover:underline flex items-center gap-1">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    Phone
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Specifications & Description Section -->
      <div v-if="part.description || (part.specifications && Object.keys(part.specifications).length > 0)" class="bg-gradient-to-b from-slate-100 to-slate-200 py-10">
        <div class="container mx-auto px-4">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Technical Specs Table -->
            <div v-if="part.specifications && Object.keys(part.specifications).length > 0" class="bg-white rounded-xl shadow-lg overflow-hidden">
              <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4">
                <h2 class="text-white font-bold uppercase tracking-wide">{{ t('sparePartDetails.technicalSpecifications', 'Technical Specifications') }}</h2>
              </div>
              <table class="w-full text-sm">
                <tbody class="divide-y divide-gray-200">
                  <tr v-for="(value, key) in part.specifications" :key="key">
                    <td class="px-4 py-3 bg-gray-50 font-medium text-gray-700 w-1/3 capitalize">{{ String(key).replace(/_/g, ' ') }}</td>
                    <td class="px-4 py-3 text-gray-900">{{ value || t('sparePartDetails.notSpecified', 'Not specified') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Description -->
            <div v-if="part.description" class="bg-white rounded-xl shadow-lg overflow-hidden" :class="{ 'lg:col-span-2': !part.specifications || Object.keys(part.specifications).length === 0 }">
              <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-4">
                <h2 class="text-white font-bold uppercase tracking-wide">{{ t('sparePartDetails.cards.description', 'Description') }}</h2>
              </div>
              <div class="p-5">
                <div class="text-sm text-gray-700 leading-relaxed" v-html="formatDescription(part.description)"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Similar Spare Parts -->
      <div class="bg-gradient-to-b from-slate-200 to-slate-300 py-10 print:hidden">
        <div class="container mx-auto px-4">
          <SimilarItemsSection 
            v-if="part"
            :category-id="part.category?.id || part.category_id" 
            :exclude-id="part.id" 
            type="spare-parts" 
          />
        </div>
      </div>

      <!-- Lightbox -->
      <Teleport to="body">
        <Transition name="fade">
          <div v-if="lightboxOpen" class="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" @click.self="closeLightbox">
            <button @click="closeLightbox" class="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
            <button v-if="partImages.length > 1" @click="prevImage" class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
            </button>
            <img :src="selectedImage" :alt="part.name" class="max-w-[90vw] max-h-[90vh] object-contain transition-transform" @click="toggleZoom" :class="{ 'scale-150 cursor-zoom-out': isZoomed, 'cursor-zoom-in': !isZoomed }" />
            <button v-if="partImages.length > 1" @click="nextImage" class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <div class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded">
              {{ selectedImageIndex + 1 }} / {{ partImages.length }}
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- Inquiry Modal -->
      <SparePartInquiryModal v-if="showInquiryModal" :part="part" @close="showInquiryModal = false" />
    </div>
  </div>
</template>

<script setup>
const { getSafeImageUrl: getImageUrlFromComposable } = useImageHandling()

definePageMeta({
  layout: 'default'
})

const route = useRoute()
const { t } = useI18n()
const config = useRuntimeConfig()

// Always use absolute Railway URL so SSR can fetch directly
const apiBase = config.public.apiBase || 'https://technomac-snc-production.up.railway.app/api/v1'

// SSR-safe data fetching — runs on server so Google sees full content
// Falls back to client-side fetch if Railway is cold-starting (502)
const { data: partData, pending: loading, error: fetchError, refresh } = await useAsyncData(
  `spare-part-${route.params.id}`,
  async () => {
    try {
      return await $fetch(`${apiBase}/spare-parts/${route.params.id}`, {
        headers: { 'Accept': 'application/json' },
        timeout: 8000,
      });
    } catch (e) {
      // Railway cold start / 502 — return null, client will re-fetch
      if (process.server) return null;
      throw e;
    }
  },
  { server: true, lazy: false }
)

const part = computed(() =>
  partData.value?.data?.data
  || partData.value?.data
  || null
)

// SSR-safe URL
const requestUrl = useRequestURL()
const currentUrl = computed(() => {
  if (process.client) return window.location.href
  return requestUrl.href
})

// SEO meta driven by SSR data
useSeoMeta({
  title: () => part.value?.name
    ? `${part.value.name} - Technomac SNC`
    : 'Spare Part Details - Technomac SNC',
  description: () => part.value?.description?.substring(0, 155)
    || 'Pièces détachées industrielles - Technomac SNC',
  ogTitle: () => part.value?.name || 'Spare Part - Technomac SNC',
  ogDescription: () => part.value?.description?.substring(0, 155) || 'Pièces détachées industrielles - Technomac SNC',
  ogUrl: () => `https://www.technomac-snc.com/spare-parts/${route.params.id}`,
  ogImage: () => part.value?.images?.[0]?.file_path
    ? getSafeImageUrl(part.value.images[0].file_path)
    : 'https://www.technomac-snc.com/Logo_Technomac-snc_PNG.png',
  twitterCard: 'summary_large_image',
})

// JSON-LD schema is registered below, after getSafeImageUrl is defined.

// UI-only state
const selectedImageIndex = ref(0)
const lightboxOpen = ref(false)
const showInquiryModal = ref(false)
const isZoomed = ref(false)

// Computed
const partImages = computed(() => {
  if (!part.value?.images || part.value.images.length === 0) {
    if (part.value?.image_url) {
      return [{ image_url: getSafeImageUrl(part.value.image_url) }]
    }
    return []
  }
  return part.value.images
})

const selectedImage = computed(() => {
  if (partImages.value.length === 0) return null
  return getImageUrl(partImages.value[selectedImageIndex.value])
})

// Methods
const getSafeImageUrl = (imagePath) => {
  return getImageUrlFromComposable(imagePath, '/images/placeholder-spare-part.svg')
}

// JSON-LD schema — registered using useHead with raw objects.
// Unhead v2 safely serializes raw objects in innerHTML for application/ld+json without breaking quotes.
watchEffect(() => {
  const p = part.value;
  if (p) {
    useHead({
      script: [
        {
          type: 'application/ld+json',
          key: 'product-schema',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: p.name,
            description: p.description || `${p.name} — Pièce détachée industrielle`,
            image: (p.images ?? []).map(img => getSafeImageUrl(img.file_path || img.image_url)).filter(Boolean),
            sku: p.part_number || String(p.id),
            brand: { '@type': 'Brand', name: p.brand?.name || 'Technomac SNC' },
            itemCondition: 'https://schema.org/UsedCondition',
            offers: {
              '@type': 'Offer',
              priceCurrency: 'EUR',
              availability: (p.stock ?? 1) > 0
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
              seller: { '@type': 'Organization', name: 'Technomac SNC', url: 'https://www.technomac-snc.com' },
            },
          })
        },
        {
          type: 'application/ld+json',
          key: 'part-breadcrumb',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://www.technomac-snc.com/' },
              { '@type': 'ListItem', position: 2, name: 'Pièces détachées', item: 'https://www.technomac-snc.com/spare-parts' },
              { '@type': 'ListItem', position: 3, name: p.name },
            ],
          })
        }
      ]
    });
  }
});


const getImageUrl = (image) => {
  if (!image) return '/images/placeholder-spare-part.svg'
  // Prefer file_path, always process through getSafeImageUrl
  if (image.file_path) return getSafeImageUrl(image.file_path)
  if (image.image_url) return getSafeImageUrl(image.image_url)
  if (image.url) return getSafeImageUrl(image.url)
  if (image.full_url) return getSafeImageUrl(image.full_url)
  return '/images/placeholder-spare-part.svg'
}

const handleImageError = (e) => { e.target.src = '/images/placeholder-spare-part.svg' }

const getStockStatusClass = (stock) => {
  if (stock === 0 || stock === undefined) return 'bg-red-100 text-red-800'
  if (stock <= 5) return 'bg-amber-100 text-amber-800'
  return 'bg-green-100 text-green-800'
}

const getStockStatusLabel = (stock) => {
  if (stock === 0 || stock === undefined) return t('sparePartDetails.outOfStock', 'Out of Stock')
  if (stock <= 5) return t('sparePartDetails.lowStock', 'Low Stock')
  return t('sparePartDetails.inStock', 'In Stock')
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price)
}

const formatDescription = (text) => text ? text.replace(/\n/g, '<br>') : ''

const prevImage = () => { selectedImageIndex.value = (selectedImageIndex.value - 1 + partImages.value.length) % partImages.value.length }
const nextImage = () => { selectedImageIndex.value = (selectedImageIndex.value + 1) % partImages.value.length }

const openLightbox = (index) => {
  selectedImageIndex.value = index
  lightboxOpen.value = true
  isZoomed.value = false
  if (process.client) document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
  lightboxOpen.value = false
  isZoomed.value = false
  if (process.client) document.body.style.overflow = ''
}

const toggleZoom = () => { isZoomed.value = !isZoomed.value }
const printPage = () => { if (process.client) window.print() }

const handleKeydown = (e) => {
  if (lightboxOpen.value) {
    if (e.key === 'ArrowLeft') prevImage()
    if (e.key === 'ArrowRight') nextImage()
    if (e.key === 'Escape') closeLightbox()
  }
}

onMounted(() => {
  if (process.client) window.addEventListener('keydown', handleKeydown)
  // Re-fetch client-side if SSR failed (Railway cold start)
  if (!partData.value) {
    refresh()
  }
})

onUnmounted(() => {
  if (process.client) {
    window.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
  }
})

// Re-fetch when navigating between spare-part pages client-side
watch(() => route.params.id, () => { refresh(); selectedImageIndex.value = 0 })
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@media print {
  .print\:hidden { display: none !important; }
}
</style>
